import * as THREE from 'three'
import { measureCadDashScale } from '@/plugins/cad/diagnostics/CadDashScaleTracker'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import { Setting } from '@/plugins/cad/module/common/setting'
import type { CadViewEnvironment } from '@/plugins/cad/module/common/utils'

interface DashScaleState {
  renderer: unknown
  material: THREE.Material
  world: THREE.Matrix4
  projection: THREE.Matrix4
  reference: THREE.Vector3
  viewport: THREE.Vector4
  dpr: number
  dashPixels: number
  value: number
}

/** 为线对象安装相机、投影、视口、DPR 和空间位置完整失效的虚线比例缓存。 */
export function installLineTypeDashScaleUpdater(object: THREE.Object3D): void {
  const lineObject = object as THREE.Object3D & {
    geometry?: THREE.BufferGeometry
    material?: {
      dashed?: boolean
      isLineMaterial?: boolean
      isLine2NodeMaterial?: boolean
      userData?: { cadDashUniforms?: unknown }
    }
  }
  if (
    !lineObject.geometry ||
    (!lineObject.material?.isLineMaterial && !lineObject.material?.isLine2NodeMaterial)
  ) {
    return
  }

  const originalOnBeforeRender = object.onBeforeRender.bind(object)
  const referencePoint = new THREE.Vector3()
  const viewport = new THREE.Vector4()
  const cache = new WeakMap<THREE.Camera, DashScaleState>()

  // WebGPU 静态批次在透视投影下使用逐实体 uniform；正交投影仍依赖 material.scale，
  // 因此批次材质也必须经过这里，才能随正交 zoom 更新。
  object.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
    originalOnBeforeRender(renderer, scene, camera, geometry, material, group)
    const currentMaterial = (object as THREE.Object3D & { material?: any }).material
    if (
      (!currentMaterial?.isLineMaterial && !currentMaterial?.isLine2NodeMaterial) ||
      !currentMaterial.dashed
    ) {
      return
    }

    measureCadDashScale(() => {
      const currentGeometry = lineObject.geometry
      if (currentGeometry && !currentGeometry.boundingSphere)
        currentGeometry.computeBoundingSphere()
      if (currentGeometry?.boundingSphere) {
        referencePoint.copy(currentGeometry.boundingSphere.center).applyMatrix4(object.matrixWorld)
      } else {
        object.getWorldPosition(referencePoint)
      }
      if (![referencePoint.x, referencePoint.y, referencePoint.z].every(Number.isFinite)) {
        object.getWorldPosition(referencePoint)
      }
      if (typeof renderer.getViewport === 'function') renderer.getViewport(viewport)
      else
        viewport.set(
          0,
          0,
          renderer.domElement?.clientWidth ?? 0,
          renderer.domElement?.clientHeight ?? 0
        )
      const dpr = renderer.getPixelRatio?.() ?? 1
      const environment: CadViewEnvironment = {
        viewWidth: viewport.z,
        viewHeight: viewport.w,
        pixelRatio: dpr,
      }
      const cached = cache.get(camera)
      const unchanged =
        cached &&
        cached.renderer === renderer &&
        cached.material === currentMaterial &&
        cached.world.equals(camera.matrixWorld) &&
        cached.projection.equals(camera.projectionMatrix) &&
        cached.reference.equals(referencePoint) &&
        cached.viewport.equals(viewport) &&
        cached.dpr === dpr &&
        cached.dashPixels === Setting.dashSize
      const value = unchanged
        ? cached.value
        : MaterialUtils.getLineTypeDashScaleByCamera(camera, environment, undefined, referencePoint)
      if (value === undefined) return
      if (!unchanged) {
        const state = cached ?? {
          renderer,
          material: currentMaterial,
          world: new THREE.Matrix4(),
          projection: new THREE.Matrix4(),
          reference: new THREE.Vector3(),
          viewport: new THREE.Vector4(),
          dpr,
          dashPixels: Setting.dashSize,
          value,
        }
        state.renderer = renderer
        state.material = currentMaterial
        state.world.copy(camera.matrixWorld)
        state.projection.copy(camera.projectionMatrix)
        state.reference.copy(referencePoint)
        state.viewport.copy(viewport)
        state.dpr = dpr
        state.dashPixels = Setting.dashSize
        state.value = value
        cache.set(camera, state)
      }
      // 材质可能被另一对象或视口使用过，命中缓存时也必须写回本对象的比例。
      MaterialUtils.updateLineTypeDashScale(currentMaterial, value)
    })
  }
}
